import React, { useEffect, useMemo, useRef, useState } from 'react';
import { theme } from './theme';
import { ChamberIcon } from './ChamberRenameModal';

/**
 * MultiCombobox — Power BI / MUI Autocomplete style multi-select.
 *
 *  - User can pick multiple values (chips show inside the input area)
 *  - User can type to filter the suggestion list
 *  - User can press Enter on a non-matching string to add a custom value
 *  - Click ✕ on a chip to remove it
 *  - Click "Clear all" to reset
 *  - Empty selection = "All"
 */

export type ComboOption = { value: string; label: string; icon?: string; description?: string };

type Props = {
  label: string;
  icon?: string;
  values: string[];          // currently selected values
  options: ComboOption[];    // suggestions (can be empty)
  placeholder?: string;
  onChange: (vals: string[]) => void;
  width?: number | string;
};

const MultiCombobox: React.FC<Props> = ({
  label, icon, values, options, placeholder, onChange, width = 240,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const labelOf = (val: string) => options.find(o => o.value === val)?.label ?? val;

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    const base = options.filter(o => !values.includes(o.value));
    if (!q) return base;
    return base.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  }, [text, options, values]);

  const add = (val: string) => {
    if (!val || values.includes(val)) return;
    onChange([...values, val]);
    setText('');
    inputRef.current?.focus();
  };
  const remove = (val: string) => {
    onChange(values.filter(v => v !== val));
  };

  return (
    <div ref={wrapRef} style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <span style={{
        fontSize: 11, fontWeight: 600, color: theme.textMuted,
        marginBottom: 6, letterSpacing: 0.1,
      }}>
        {label}
      </span>

      <div
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
        style={{
          width,
          minHeight: 38,
          padding: '5px 32px 5px 10px',
          borderRadius: 8,
          border: open ? '1px solid #2563eb' : '1px solid #e5eaf2',
          background: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 6,
          cursor: 'text',
          position: 'relative',
          boxShadow: open ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
          transition: 'all .15s ease',
          boxSizing: 'border-box',
          fontFamily: 'inherit',
        }}
      >
        {values.length === 0 && !text && (
          <span style={{ color: theme.textMuted, fontSize: 13, padding: '0 6px' }}>
            {placeholder ?? `All ${label}`}
          </span>
        )}

        {values.map(v => (
          <span key={v} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 4px 3px 9px',
            borderRadius: 6,
            background: '#f1f5fb',
            color: theme.textPrimary,
            border: '1px solid #e0e7f0',
            fontSize: 12,
            fontWeight: 500,
            maxWidth: 160,
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {labelOf(v)}
            </span>
            <span
              onClick={(e) => { e.stopPropagation(); remove(v); }}
              style={{ cursor: 'pointer', fontWeight: 800, padding: '0 2px' }}
            >×</span>
          </span>
        ))}

        <input
          ref={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const exact = filtered.find(o => o.label.toLowerCase() === text.trim().toLowerCase());
              if (exact) add(exact.value);
              else if (text.trim()) add(text.trim());
            } else if (e.key === 'Backspace' && !text && values.length) {
              remove(values[values.length - 1]);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          style={{
            flex: 1,
            minWidth: 60,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: theme.textPrimary,
            fontSize: 13,
            fontWeight: 600,
            padding: '4px 4px',
          }}
        />

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (values.length) onChange([]);
            else { setOpen(o => !o); inputRef.current?.focus(); }
          }}
          style={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', color: theme.textSecondary,
            cursor: 'pointer', fontSize: 14, padding: '4px 8px',
          }}
          title={values.length ? 'Clear all' : 'Open'}
        >
          {values.length ? '✕' : '▾'}
        </button>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          width,
          background: '#fff',
          border: '1px solid #d3dbe8',
          borderRadius: 10,
          boxShadow: '0 16px 40px rgba(20,40,80,0.16)',
          maxHeight: 280,
          overflowY: 'auto',
          zIndex: 50,
        }}>
          {filtered.length === 0 && text.trim() && (
            <div
              onClick={() => add(text.trim())}
              style={{ ...optionStyle, fontStyle: 'italic', color: theme.accents.amber }}
            >
              + Add custom value: "{text.trim()}"
            </div>
          )}
          {/* CHAMBER_ROW_RENDER */}
          {filtered.map(o => (
            <div key={o.value}
              onClick={() => add(o.value)}
              style={{
                ...optionStyle,
                display: 'flex', alignItems: 'center', gap: 10,
                padding: o.description ? '8px 12px' : optionStyle.padding,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#eef4fc')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
            >
              {o.icon ? <ChamberIcon icon={o.icon} /> : null}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 700, color: theme.textPrimary, fontSize: 13,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{o.label}</div>
                {o.description ? (
                  <div style={{
                    fontSize: 11, color: theme.textMuted, marginTop: 1, fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{o.description}</div>
                ) : null}
              </div>
            </div>
          ))}
          {filtered.length === 0 && !text.trim() && (
            <div style={{ padding: 12, color: theme.textMuted, fontSize: 12, fontStyle: 'italic' }}>
              {options.length === 0 ? 'No options available — type to add custom' : 'All options selected'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const optionStyle: React.CSSProperties = {
  padding: '9px 14px',
  fontSize: 13,
  fontWeight: 600,
  color: theme.textPrimary,
  cursor: 'pointer',
  background: '#fff',
  borderBottom: '1px solid #f1f4f9',
};

export default MultiCombobox;
